import React, { useState } from 'react';
import { usePIM } from '../context/PIMContext';
import { Input } from './ui/Input';
import { Settings, RefreshCw } from 'lucide-react';
import { PRODUCT_TYPES } from '../config';

import { ColorManagementModal } from './modals/ColorManagementModal';
import { CategoryManagementModal } from './modals/CategoryManagementModal';
import { StatusManagementModal } from './modals/StatusManagementModal';

export const ProductForm = () => {
    const { colors, categories, statuses, productData, setProductData, canEditContent, canManageSettings, currentUser, charges, activeCharge, setActiveCharge } = usePIM();
    const [isColorModalOpen, setIsColorModalOpen] = useState(false);
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
    const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);

    if (!productData) {
        console.error("ProductForm: productData is missing from context");
        return <div className="p-4 text-red-500">Error: Product Data not loaded</div>;
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        setProductData(prev => ({ ...prev, [name]: value }));
    };

    const handleTypeChange = (type) => {
        const currentTypes = productData.product_types || [];
        let newTypes;

        if (currentTypes.includes(type)) {
            newTypes = currentTypes.filter(t => t !== type);
        } else {
            newTypes = [...currentTypes, type];
        }

        // Logic: Component and Accessory are mutually exclusive
        if (type === 'component') {
            if (newTypes.includes('component')) {
                // If component added, remove accessory
                newTypes = newTypes.filter(t => t !== 'accessory');
            }
        }
        if (type === 'accessory') {
            if (newTypes.includes('component')) {
                // If accessory added (via type list), remove component?
                // Or block? User said: "Wenn component gewählt -> Accessory-Checkbox disabled + auf false setzen"
                // So if component is present, accessory cannot be added.
                // But in handleTypeChange, if we just toggled accessory...
                if (productData.product_types.includes('component')) return; // Prevent adding accessory
            }
        }

        // Sync logic with legacy isAccessory flag
        const isAcc = newTypes.includes('accessory');

        setProductData(prev => ({
            ...prev,
            product_types: newTypes,
            isAccessory: isAcc
        }));
    };

    return (
        <div className="w-full h-full flex flex-col gap-3">
            <h3 className="text-stone-300 font-semibold text-lg shrink-0 flex items-center justify-between">
                <span>Product details</span>
            </h3>
            
            <div className="grid grid-cols-2 gap-3 overflow-y-auto pr-2 pb-2">
                {/* MODEL Card */}
                <div className="p-1 flex flex-col justify-center relative">
                    <label className="text-xs font-medium text-stone-400 uppercase mb-1">MODEL:</label>
                    <Input
                        name="model"
                        value={productData.model}
                        onChange={handleChange}
                        className="bg-white/5 border-white/10 py-1.5 rounded-md backdrop-blur-sm hover:border-white/20 focus:border-blue-400/50 focus:bg-white/10 focus:shadow-[0_0_15px_rgba(59,130,246,0.2)] transition-all"
                        disabled={!canEditContent}
                    />
                </div>

                {/* SKU Card */}
                <div className="p-1 flex flex-col justify-center relative min-w-0">
                    <label className="text-xs font-medium text-stone-400 uppercase mb-1">SKU*:</label>
                    <Input
                        name="sku"
                        value={productData.sku}
                        onChange={handleChange}
                        title={productData.sku}
                        className="bg-white/5 border-white/10 py-1.5 rounded-md backdrop-blur-sm hover:border-white/20 focus:border-blue-400/50 focus:bg-white/10 focus:shadow-[0_0_15px_rgba(59,130,246,0.2)] transition-all font-mono text-xs select-all"
                        disabled={!canEditContent}
                    />
                </div>

                {/* EAN Card */}
                <div className="p-1 flex flex-col justify-center relative">
                    <label className="text-xs font-medium text-stone-400 uppercase mb-1">EAN (GTIN):</label>
                    <Input
                        name="ean"
                        value={productData.ean}
                        onChange={handleChange}
                        className="bg-white/5 border-white/10 py-1.5 rounded-md backdrop-blur-sm hover:border-white/20 focus:border-blue-400/50 focus:bg-white/10 focus:shadow-[0_0_15px_rgba(59,130,246,0.2)] transition-all"
                        disabled={!canEditContent}
                    />
                </div>

                {/* COLOR Card */}
                <div className="p-1 flex flex-col justify-center relative">
                    <label className="text-xs font-medium text-stone-400 uppercase mb-1">COLOR:</label>
                    <div className="relative w-full">
                        <select
                            name="color"
                            value={productData.color}
                            onChange={handleChange}
                            disabled={!canEditContent}
                            className="w-full bg-white/5 border border-white/10 rounded-md px-3 pr-10 py-1.5 text-sm text-stone-200 focus:outline-none focus:border-blue-400/50 focus:bg-white/10 focus:shadow-[0_0_15px_rgba(59,130,246,0.2)] hover:border-white/20 appearance-none transition-all backdrop-blur-sm"
                        >
                            <option value="" className="bg-[#0c1328] text-stone-200">Select Color</option>
                            {colors.map(c => (
                                <option key={c.id || c} value={c.name || c} className="bg-[#0c1328] text-stone-200">
                                    {c.name || c}{c.pantone ? ` - ${c.pantone}` : ''}
                                </option>
                            ))}
                        </select>
                        {canManageSettings && (
                            <div className="absolute right-0 top-0 bottom-0 flex items-center pr-1 pointer-events-none">
                                <div className="h-4/6 w-[1px] bg-white/10 mr-1"></div>
                                <button
                                    onClick={() => setIsColorModalOpen(true)}
                                    className="p-1 text-stone-400 hover:text-blue-300 hover:bg-white/10 rounded transition-all pointer-events-auto"
                                >
                                    <Settings size={14} />
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* CATEGORY Card */}
                <div className="p-1 flex flex-col justify-center relative">
                    <label className="text-xs font-medium text-stone-400 uppercase mb-1">CATEGORY:</label>
                    <div className="relative w-full">
                        <select
                            name="category"
                            value={productData.category}
                            onChange={handleChange}
                            disabled={!canEditContent}
                            className="w-full bg-white/5 border border-white/10 rounded-md px-3 pr-10 py-1.5 text-sm text-stone-200 focus:outline-none focus:border-blue-400/50 focus:bg-white/10 focus:shadow-[0_0_15px_rgba(59,130,246,0.2)] hover:border-white/20 appearance-none transition-all backdrop-blur-sm"
                        >
                            <option value="" className="bg-[#0c1328] text-stone-200">Select Category</option>
                            {categories.map(c => (
                                <option key={c.id || c} value={c.name || c} className="bg-[#0c1328] text-stone-200">
                                    {c.name || c}
                                </option>
                            ))}
                        </select>
                        {canManageSettings && (
                            <div className="absolute right-0 top-0 bottom-0 flex items-center pr-1 pointer-events-none">
                                <div className="h-4/6 w-[1px] bg-white/10 mr-1"></div>
                                <button
                                    onClick={() => setIsCategoryModalOpen(true)}
                                    className="p-1 text-stone-400 hover:text-blue-300 hover:bg-white/10 rounded transition-all pointer-events-auto"
                                >
                                    <Settings size={14} />
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* STATUS Card */}
                <div className="p-1 flex flex-col justify-center relative">
                    <label className="text-xs font-medium text-stone-400 uppercase mb-1">STATUS:</label>
                    <div className="relative w-full">
                        <select
                            name="status"
                            value={productData.status}
                            onChange={handleChange}
                            style={{
                                borderColor: statuses.find(s => s.name === productData.status)?.hex_code || '',
                                backgroundColor: statuses.find(s => s.name === productData.status)?.hex_code ? `${statuses.find(s => s.name === productData.status)?.hex_code}10` : '', boxShadow: statuses.find(s => s.name === productData.status)?.hex_code ? `0 0 10px ${statuses.find(s => s.name === productData.status)?.hex_code}40` : ''
                            }}
                            className="w-full bg-white/5 border border-white/10 rounded-md px-3 pr-10 py-1.5 text-sm text-stone-200 focus:outline-none focus:border-blue-400/50 focus:bg-white/10 focus:shadow-[0_0_15px_rgba(59,130,246,0.2)] hover:border-white/20 appearance-none transition-all backdrop-blur-sm"
                            disabled={!canEditContent}
                        >
                            <option value="" className="bg-[#0c1328] text-stone-200">Select Status</option>
                            {statuses.map(s => (
                                <option key={s.id || s} value={s.name || s} className="bg-[#0c1328] text-stone-200">
                                    {s.name || s}
                                </option>
                            ))}
                        </select>
                        {canManageSettings && (
                            <div className="absolute right-0 top-0 bottom-0 flex items-center pr-1 pointer-events-none">
                                <div className="h-4/6 w-[1px] bg-white/10 mr-1"></div>
                                <button
                                    onClick={() => setIsStatusModalOpen(true)}
                                    className="p-1 text-stone-400 hover:text-blue-300 hover:bg-white/10 rounded transition-all pointer-events-auto"
                                >
                                    <Settings size={14} />
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* CHARGE Selector Card */}
                <div className="p-1 flex flex-col justify-center relative min-w-0">
                    <div className="flex justify-between items-center mb-1">
                        <label className="text-xs font-medium text-stone-400 uppercase">CHARGE:</label>
                        <span className="text-xs text-stone-500">{activeCharge?.id || ''}</span>
                    </div>
                    <div className="relative w-full">
                        <select
                            className="w-full bg-white/5 border border-white/10 rounded px-3 py-1.5 text-sm text-stone-200 focus:outline-none focus:border-blue-600 appearance-none transition-colors backdrop-blur-sm hover:border-white/20"
                            value={activeCharge?.id || ''}
                            onChange={(e) => {
                                const selected = charges.find(c => c.id == e.target.value);
                                if (selected) setActiveCharge(selected);
                            }}
                            style={{
                                borderColor: activeCharge ? statuses.find(s => s.name === activeCharge.status)?.hex_code || '' : '',
                                backgroundColor: activeCharge && statuses.find(s => s.name === activeCharge.status)?.hex_code ? `${statuses.find(s => s.name === activeCharge.status)?.hex_code}20` : ''
                            }}
                        >
                            <option value="" className="bg-[#0c1328] text-stone-200" style={{ backgroundColor: '#0c1328' }}>Select Charge</option>
                            {charges.map(charge => (
                                <option key={charge.id} value={charge.id} className="bg-[#0c1328] text-stone-200" style={{ backgroundColor: '#0c1328' }}>
                                    {charge.charge_id} ({charge.status})
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* NOTES Card */}
                <div className="p-1 flex flex-col justify-center relative min-w-0">
                    <label className="text-xs font-medium text-stone-400 uppercase mb-1">NOTES:</label>
                    <textarea
                        name="notes"
                        value={productData.notes || ''}
                        onChange={handleChange}
                        disabled={!canEditContent}
                        placeholder="Product notes..."
                        className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-1.5 text-xs text-stone-200 placeholder-stone-500 focus:outline-none focus:border-blue-400/50 focus:bg-white/10 focus:shadow-[0_0_15px_rgba(59,130,246,0.2)] hover:border-white/20 transition-all backdrop-blur-sm resize-none leading-relaxed h-[38px]"
                    />
                </div>

                {/* PRODUCT TYPES Card */}
                <div className="col-span-2 p-1 flex flex-col justify-center relative">
                    <label className="text-xs font-medium text-stone-400 uppercase mb-1">PRODUCT TYPES:</label>
                    <div className="flex flex-wrap items-center gap-1.5 w-full">
                        {PRODUCT_TYPES.filter(t => t !== 'normal').map(type => (
                            <label key={type} className={`
                                flex items-center justify-center px-2 py-1 rounded-md border transition-all cursor-pointer select-none
                                ${(productData.product_types || []).includes(type)
                                    ? 'bg-blue-900/30 border-blue-600 text-blue-200 backdrop-blur-sm shadow-[0_0_10px_rgba(30,58,138,0.2)]'
                                    : 'bg-white/5 border-white/10 text-stone-400 hover:border-blue-400/40 hover:bg-white/10 hover:shadow-[0_0_15px_rgba(59,130,246,0.2)] backdrop-blur-sm'
                                }
                                ${type === 'accessory' && (productData.product_types || []).includes('component') ? 'opacity-50 cursor-not-allowed' : ''}
                            `}>
                                <input
                                    type="checkbox"
                                    checked={(productData.product_types || []).includes(type)}
                                    onChange={() => handleTypeChange(type)}
                                    disabled={!canEditContent || (type === 'accessory' && (productData.product_types || []).includes('component'))}
                                    className="hidden"
                                />
                                <span className="text-[10px] font-normal uppercase whitespace-nowrap">{type.replace('_', ' ')}</span>
                            </label>
                        ))}
                    </div>
                </div>
            </div>

            <ColorManagementModal isOpen={isColorModalOpen} onClose={() => setIsColorModalOpen(false)} />
            <CategoryManagementModal isOpen={isCategoryModalOpen} onClose={() => setIsCategoryModalOpen(false)} />
            <StatusManagementModal isOpen={isStatusModalOpen} onClose={() => setIsStatusModalOpen(false)} />
        </div>
    );
};
